/**
* PriceBand — 3-tier service variant display.
*
* Prices are quoted per case, so each tier shows what is included and the
* turnaround, and points the visitor to phone / LINE / email for a quote.
*/
import type { Product } from "@/data/products";
import { quoteHint, quoteLabel, type QuoteLocale } from "@/config/pricing";

/** "N working days" per shipped locale. */
const WORKING_DAYS: Record<QuoteLocale, (n: number) => string> = {
  th: (n) => `${n} วันทำการ`,
  en: (n) => `${n} working day${n === 1 ? "" : "s"}`,
  zh: (n) => `${n} 个工作日`,
  ja: (n) => `${n} 営業日`,
  ko: (n) => `${n} 영업일`,
  vi: (n) => `${n} ngày làm việc`,
  id: (n) => `${n} hari kerja`,
};

export function PriceBand({
  product,
  locale = "th",
}: {
  product: Product;
  locale?: QuoteLocale;
}) {
  void WORKING_DAYS;
  return (
    <div className="grid gap-3 sm:grid-cols-3">
      {product.variants.map((v) => (
        <div
          key={v.id}
          className="rounded-lg border border-border bg-card p-4 shadow-sm"
        >
          <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {v.labelEn}
          </div>
          {locale === "th" ? (
            <div className="mt-1 text-lg font-bold text-foreground">{v.label}</div>
          ) : (
            <div className="mt-1 text-lg font-bold text-foreground">{v.labelEn}</div>
          )}

          <div className="mt-2 text-base font-extrabold text-primary">
            {quoteLabel(locale)}
          </div>
          <div className="text-xs text-muted-foreground">{quoteHint(locale)}</div>

          <ul className="mt-3 space-y-1 text-sm text-foreground/80">
            {v.includes.map((inc) => (
              <li key={inc} className="flex gap-2">
                <span aria-hidden className="text-primary">✓</span>
                <span>{inc}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}
